import {
    type ReactNode,
    Fragment,
    memo
} from "react";
import {
    View
} from "react-native";
import type {
    DataTableRowProps
} from "./type";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import stylesheet, {
    getDataTableColumnProps,
    useStyles
} from "../../components/content/stylesheet";
import {
    useDataTableContext
} from "../../context";
import {
    DataTableSelectCell
} from "../../components/selectColumn";
import {
    TABLE_BODY_VARIANTS
} from "../../../table/stylesheet";
import DataTableExpandTrigger from "../expandTrigger";
import DataTableDetail from "../detail";
import Table from "../../../table";
import Text from "../../../text";

const DataTableRow = memo<DataTableRowProps>(({
    customTheme,
    isExpanded,
    isSelected,
    baseId,
    row
}) => {
    const context = useDataTableContext({
        part: "Content"
    });

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme ?? context?.customTheme);

    const {
        lead: leadDynamicStyle
    } = useStyles({
        selectWidth: context?.selectWidth ?? 0,
        spaces
    });

    if(!context) {
        return null;
    }

    const hasSelect = context.selectionBehaviour !== "none";

    const getStickyProps = (columnIndex: number) => {
        if(columnIndex !== 0 || !hasSelect || !context.isFirstColumnSticky) {
            return {};
        }

        return {
            stickyOffset: context.selectWidth,
            isSticky: true
        };
    };

    const getColumnProps = (columnId: string) => {
        const column = context.columns.find((item) => item.id === columnId);

        return column ? getDataTableColumnProps({
            width: context.columnWidths[columnId],
            column
        }) : {};
    };

    const renderCell = (node: ReactNode, columnIndex: number) => {
        if(columnIndex !== 0 || !context.isExpandable) {
            return node;
        }

        const content = typeof node === "string" || typeof node === "number" ? <Text
            variant={TABLE_BODY_VARIANTS[context.size]}
            customTheme={customTheme}
            numberOfLines={1}
            color="high"
        >
            {node}
        </Text> : node;

        return <View
            style={[
                stylesheet.lead,
                leadDynamicStyle
            ]}
        >
            <DataTableExpandTrigger
                controlsId={`${baseId}-detail-${row.id}`}
                isExpandable={row.isExpandable}
                customTheme={customTheme}
                isExpanded={isExpanded}
                label={row.label}
                rowId={row.id}
            />
            <View
                style={stylesheet.leadContent}
            >
                {content}
            </View>
        </View>;
    };

    return <Fragment>
        <Table.Row
            onPress={context.onRowPress ? () => context.onRowPress?.(row.id) : undefined}
            style={isExpanded ? stylesheet.rowOpen : undefined}
            isSelected={isSelected}
        >
            {
                hasSelect ?
                    <DataTableSelectCell
                        customTheme={customTheme}
                        isChecked={isSelected}
                        rowId={row.id}
                    />
                :
                    null
            }
            {
                context.columns.map((column, columnIndex) => <Table.Cell
                    {...getColumnProps(column.id)}
                    {...getStickyProps(columnIndex)}
                    key={column.id}
                >
                    {renderCell(row.cells[column.id], columnIndex)}
                </Table.Cell>)
            }
        </Table.Row>
        {
            row.isExpandable ?
                <DataTableDetail
                    colSpan={context.columns.length + (hasSelect ? 1 : 0)}
                    controlsId={`${baseId}-detail-${row.id}`}
                    customTheme={customTheme}
                    isExpanded={isExpanded}
                    detail={row.detail}
                    label={row.label}
                />
            :
                null
        }
    </Fragment>;
});
export default DataTableRow;
